import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { MatchPublic, MatchStage, PredictionPublic, TeamPublic } from '@mondy/shared';
import { isMatchLive } from '@mondy/shared';
import { api } from '@/api';
import { EarlyPredictionUpgradeCTA } from '@/components/EarlyPredictionUpgradeCTA';
import { MatchDayList } from '@/components/MatchDayList';
import { Spinner } from '@/components/Spinner';
import { TeamSearch } from '@/components/TeamSearch';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cn } from '@/lib/cn';
import { STAGE_LABELS, STAGE_ORDER } from '@/lib/format';

type StageFilter = 'all' | MatchStage;
type GroupFilter = 'all' | string;

const GROUP_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

const HIDE_PAST_STORAGE_KEY = 'mondy_hide_past';
const HIDE_PREDICTED_STORAGE_KEY = 'mondy_hide_predicted';
const ONLY_LIVE_STORAGE_KEY = 'mondy_only_live';

function readHidePastPref(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(HIDE_PAST_STORAGE_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // localStorage may throw in private browsing / disabled storage modes —
    // fall through to the default.
  }
  return true;
}

function readHidePredictedPref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(HIDE_PREDICTED_STORAGE_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // localStorage may throw in private browsing / disabled storage modes —
    // fall through to the default.
  }
  return false;
}

function readOnlyLivePref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(ONLY_LIVE_STORAGE_KEY);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // localStorage may throw in private browsing / disabled storage modes —
    // fall through to the default.
  }
  return false;
}

interface MatchListResponse {
  matches: MatchPublic[];
}

const SCROLL_ANCHOR_ID = 'schedule-scroll-target';

export function SchedulePage() {
  const [stage, setStage] = useState<StageFilter>('all');
  const [group, setGroup] = useState<GroupFilter>('all');
  // Read synchronously so the first render reflects the saved preference
  // rather than flashing the default for one paint.
  const [hidePast, setHidePast] = useState<boolean>(readHidePastPref);
  const [hidePredicted, setHidePredicted] = useState<boolean>(readHidePredictedPref);
  const [onlyLive, setOnlyLive] = useState<boolean>(readOnlyLivePref);
  const user = useCurrentUser();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(HIDE_PAST_STORAGE_KEY, hidePast ? '1' : '0');
    } catch {
      // Ignore persistence failures (private mode, quota, etc.).
    }
  }, [hidePast]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(HIDE_PREDICTED_STORAGE_KEY, hidePredicted ? '1' : '0');
    } catch {
      // Ignore persistence failures (private mode, quota, etc.).
    }
  }, [hidePredicted]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ONLY_LIVE_STORAGE_KEY, onlyLive ? '1' : '0');
    } catch {
      // Ignore persistence failures (private mode, quota, etc.).
    }
  }, [onlyLive]);

  const query = useQuery({
    queryKey: ['matches', { stage, group }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (stage !== 'all') params.set('stage', stage);
      if (group !== 'all') params.set('group', group);
      const qs = params.toString();
      return api.get<MatchListResponse>(`/api/matches${qs ? `?${qs}` : ''}`);
    },
  });

  const predictionsQuery = useQuery({
    queryKey: ['predictions', 'mine'],
    queryFn: () => api.get<{ predictions: PredictionPublic[] }>('/api/predictions/mine'),
    enabled: !!user,
  });

  const teamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<{ teams: TeamPublic[] }>('/api/teams'),
    staleTime: 60 * 60 * 1000,
  });

  const predictionsByMatch = useMemo(() => {
    const map = new Map<string, PredictionPublic>();
    if (predictionsQuery.data) {
      for (const p of predictionsQuery.data.predictions) {
        map.set(p.matchId, p);
      }
    }
    return map;
  }, [predictionsQuery.data]);

  const visibleMatches = useMemo(() => {
    const all = query.data?.matches ?? [];
    return all.filter((m) => {
      if (onlyLive && !isMatchLive(m)) return false;
      if (hidePast && m.status === 'finished') return false;
      if (hidePredicted && predictionsByMatch.has(m.id)) return false;
      return true;
    });
  }, [query.data, onlyLive, hidePast, hidePredicted, predictionsByMatch]);

  // Autoscroll once to the first live match, or the next upcoming match if none
  // are live. Computed from whatever the active filters leave visible.
  const scrollTargetMatchId = useMemo(() => {
    const byKickoff = (a: MatchPublic, b: MatchPublic) =>
      new Date(a.kickoffUtc).getTime() - new Date(b.kickoffUtc).getTime();
    const live = visibleMatches.filter(isMatchLive).sort(byKickoff)[0];
    if (live) return live.id;
    const now = Date.now();
    const upcoming = visibleMatches
      .filter((m) => new Date(m.kickoffUtc).getTime() >= now)
      .sort(byKickoff)[0];
    return upcoming?.id;
  }, [visibleMatches]);

  const didScrollRef = useRef(false);
  useEffect(() => {
    if (didScrollRef.current || !scrollTargetMatchId) return;
    const el = document.getElementById(SCROLL_ANCHOR_ID);
    if (!el) return;
    didScrollRef.current = true;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [scrollTargetMatchId]);

  return (
    <>
      {/* Lift the page-head stacking context above the sibling pills and
          match list (all `main.app-main > *` at z-index 1) so the search
          dropdown, which is trapped inside this context, can overlay them. */}
      <div className="page-head" style={{ position: 'relative', zIndex: 30 }}>
        <div className="page-title">
          <div className="k">All 104 matches · Times in your local tz</div>
          <h1>
            The <span className="emph">schedule</span>.
          </h1>
        </div>
        <TeamSearch teams={teamsQuery.data?.teams ?? []} />
      </div>

      <EarlyPredictionUpgradeCTA />

      <div className="pills pills-stages">
        <button
          type="button"
          className={cn('pill', stage === 'all' && 'active')}
          onClick={() => {
            setStage('all');
            setGroup('all');
          }}
        >
          All stages
        </button>
        {STAGE_ORDER.map((s) => (
          <button
            type="button"
            key={s}
            className={cn('pill', stage === s && 'active')}
            onClick={() => {
              setStage(s);
              if (s !== 'group') setGroup('all');
            }}
          >
            {STAGE_LABELS[s]}
          </button>
        ))}
      </div>
      <select
        className="pills-stage-dropdown"
        aria-label="Stage"
        value={stage}
        onChange={(e) => {
          const v = e.target.value as StageFilter;
          if (v === 'all') {
            setStage('all');
            setGroup('all');
          } else {
            setStage(v);
            if (v !== 'group') setGroup('all');
          }
        }}
      >
        <option value="all">All stages</option>
        {STAGE_ORDER.map((s) => (
          <option key={s} value={s}>
            {STAGE_LABELS[s]}
          </option>
        ))}
      </select>

      {stage === 'group' ? (
        <>
          <div className="pills pills-groups">
            <button
              type="button"
              className={cn('pill', group === 'all' && 'active')}
              onClick={() => setGroup('all')}
            >
              All groups
            </button>
            {GROUP_LETTERS.map((g) => (
              <button
                type="button"
                key={g}
                className={cn('pill', group === g && 'active')}
                onClick={() => setGroup(g)}
              >
                {g}
              </button>
            ))}
          </div>
          <select
            className="pills-stage-dropdown pills-group-dropdown"
            aria-label="Group"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
          >
            <option value="all">All groups</option>
            {GROUP_LETTERS.map((g) => (
              <option key={g} value={g}>
                Group {g}
              </option>
            ))}
          </select>
        </>
      ) : null}

      <div className="pills pills-toggles">
        <button
          type="button"
          role="switch"
          aria-checked={onlyLive}
          className={cn('pill', onlyLive && 'active')}
          onClick={() => setOnlyLive((v) => !v)}
        >
          Live
        </button>
        {user ? (
          <button
            type="button"
            role="switch"
            aria-checked={hidePredicted}
            className={cn('pill', hidePredicted && 'active')}
            onClick={() => setHidePredicted((v) => !v)}
          >
            Not predicted
          </button>
        ) : null}
        <button
          type="button"
          role="switch"
          aria-checked={hidePast}
          className={cn('pill', hidePast && 'active')}
          onClick={() => setHidePast((v) => !v)}
        >
          Hide past
        </button>
      </div>

      {query.isLoading ? (
        <Spinner fullScreen label="Loading matches" />
      ) : query.isError ? (
        <p
          style={{
            padding: '48px 0',
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--danger)',
          }}
        >
          Couldn't load matches. Try again in a moment.
        </p>
      ) : visibleMatches.length === 0 ? (
        <p
          style={{
            padding: '48px 0',
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--fg-muted)',
          }}
        >
          No matches match those filters.
        </p>
      ) : (
        <MatchDayList
          matches={visibleMatches}
          predictionsByMatch={predictionsByMatch}
          scrollTargetMatchId={scrollTargetMatchId}
          scrollAnchorId={SCROLL_ANCHOR_ID}
        />
      )}
    </>
  );
}
